import React, { useState, useEffect, useRef } from 'react';
import { Search, Upload, Trash2, Image, Filter, Download, Plus, X, Check, Edit2, Copy } from 'lucide-react';
import { fetchOrganizationMedia, saveOrganizationMedia, deleteOrganizationMedia, updateOrganizationMedia } from '../../utils/api';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';

/**
 * MediaLibraryTab - Full media library management for organization
 * Used as a tab in CuratorCommunicationsHub
 */
const MediaLibraryTab = ({ organizationId, organizationName }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsageType, setSelectedUsageType] = useState('all');
  const [selectedItems, setSelectedItems] = useState([]);
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', usage_type: 'general', tags: '' });
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const filterRef = useRef(null);

  // Close filter menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowFilterMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load organization media on mount
  useEffect(() => {
    if (organizationId) {
      loadMedia();
    }
  }, [organizationId, selectedUsageType]);

  const loadMedia = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (selectedUsageType && selectedUsageType !== 'all') {
        filters.usageType = selectedUsageType;
      }
      const data = await fetchOrganizationMedia(organizationId, filters);
      setMedia(data || []);
    } catch (error) {
      console.error('Error loading organization media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      for (const file of files) {
        if (!file.type.startsWith('image/')) {
          console.warn(`Skipping non-image file: ${file.name}`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          alert(`File ${file.name} is too large (max 10MB)`);
          continue;
        }

        const result = await uploadToCloudinary(file, 'image', 'organization-media');
        
        await saveOrganizationMedia({
          organization_id: organizationId,
          type: 'image',
          source: result.url,
          title: file.name.replace(/\.[^/.]+$/, ''),
          usage_type: 'general',
          is_downloadable: true
        });
      }
      await loadMedia();
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload some files. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (elementId, e) => {
    if (e) e.stopPropagation();
    if (!confirm('Are you sure you want to delete this media item?')) return;
    
    try {
      await deleteOrganizationMedia(elementId);
      setMedia(prev => prev.filter(item => item.element_id !== elementId));
      setSelectedItems(prev => prev.filter(id => id !== elementId));
    } catch (error) {
      console.error('Error deleting media:', error);
      alert('Failed to delete media item.');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    if (!confirm(`Delete ${selectedItems.length} selected items?`)) return;

    try {
      for (const elementId of selectedItems) {
        await deleteOrganizationMedia(elementId);
      }
      setMedia(prev => prev.filter(item => !selectedItems.includes(item.element_id)));
      setSelectedItems([]);
    } catch (error) {
      console.error('Error bulk deleting media:', error);
      alert('Failed to delete some items.');
    }
  };

  const toggleSelect = (elementId) => {
    setSelectedItems(prev => 
      prev.includes(elementId) 
        ? prev.filter(id => id !== elementId)
        : [...prev, elementId]
    );
  };

  const copyUrl = (source, e) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(source);
    alert('URL copied to clipboard!');
  };

  const openEditModal = (item, e) => {
    if (e) e.stopPropagation();
    setEditingItem(item);
    setEditForm({
      title: item.title || '',
      description: item.description || '',
      usage_type: item.usage_type || 'general',
      tags: (item.tags || []).join(', ')
    });
  };

  const handleSaveEdit = async () => {
    if (!editingItem) return;
    setSaving(true);
    try {
      const tagsArray = editForm.tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      await updateOrganizationMedia(editingItem.element_id, {
        title: editForm.title,
        description: editForm.description,
        usage_type: editForm.usage_type,
        tags: tagsArray
      });

      // Update local state
      setMedia(prev => prev.map(item => 
        item.element_id === editingItem.element_id
          ? { ...item, ...editForm, tags: tagsArray }
          : item
      ));
      setEditingItem(null);
    } catch (error) {
      console.error('Error updating media:', error);
      alert('Failed to update media item.');
    } finally {
      setSaving(false);
    }
  };

  const getThumbnailUrl = (source) => {
    if (!source || !source.includes('cloudinary.com')) return source;
    const uploadIndex = source.indexOf('/upload/');
    if (uploadIndex === -1) return source;
    const beforeUpload = source.substring(0, uploadIndex + 8);
    const afterUpload = source.substring(uploadIndex + 8);
    return `${beforeUpload}t_square-150/${afterUpload}`;
  };

  const filteredMedia = media.filter(item => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (item.description || '').toLowerCase().includes(searchLower) ||
      (item.title || '').toLowerCase().includes(searchLower) ||
      (item.tags || []).some(tag => tag.toLowerCase().includes(searchLower))
    );
  });

  const usageTypes = [
    { value: 'all', label: 'All Types' },
    { value: 'promo', label: 'Promo' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'attachment', label: 'Attachment' },
    { value: 'banner', label: 'Banner' },
    { value: 'logo', label: 'Logo' },
    { value: 'general', label: 'General' }
  ];

  const getUsageColor = (type) => {
    const colors = {
      promo: '#7c3aed',
      social_media: '#3b82f6',
      attachment: '#10b981',
      banner: '#f59e0b',
      logo: '#ec4899',
      general: '#6b7280'
    };
    return colors[type] || '#6b7280';
  };

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h3 style={{ 
            fontSize: '1.25rem', 
            fontWeight: '600', 
            color: '#111827', 
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <Image style={{ height: '1.25rem', width: '1.25rem', color: '#7c3aed' }} />
            Media Library
            {organizationName && (
              <span style={{ fontSize: '0.875rem', fontWeight: '400', color: '#6b7280' }}>
                for {organizationName}
              </span>
            )}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0.25rem 0 0 0' }}>
            {media.length} items • Upload images to use in emails and templates
          </p>
        </div>
        
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#7c3aed',
            color: 'white',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500'
          }}
        >
          {uploading ? 'Uploading...' : <><Plus size={16} /> Upload Images</>}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>

      {/* Toolbar - Fixed layout */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        marginBottom: '1rem'
      }}>
        {/* Search */}
        <div style={{ position: 'relative', width: '250px', flexShrink: 0 }}>
          <Search 
            size={16} 
            style={{ 
              position: 'absolute', 
              left: '0.75rem', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#9ca3af'
            }} 
          />
          <input
            type="text"
            placeholder="Search media..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem 0.5rem 2.25rem',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              fontSize: '0.875rem'
            }}
          />
        </div>

        {/* Filter Icon Button with Dropdown */}
        <div ref={filterRef} style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => setShowFilterMenu(!showFilterMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              border: '1px solid #e5e7eb',
              borderRadius: '0.375rem',
              backgroundColor: selectedUsageType !== 'all' ? '#f3e8ff' : 'white',
              cursor: 'pointer',
              color: selectedUsageType !== 'all' ? '#7c3aed' : '#6b7280'
            }}
            title="Filter by type"
          >
            <Filter size={16} />
          </button>
          
          {showFilterMenu && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              marginTop: '0.25rem',
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              zIndex: 100,
              minWidth: '150px',
              overflow: 'hidden'
            }}>
              {usageTypes.map(type => (
                <button
                  key={type.value}
                  onClick={() => {
                    setSelectedUsageType(type.value);
                    setShowFilterMenu(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: 'none',
                    backgroundColor: selectedUsageType === type.value ? '#f3e8ff' : 'transparent',
                    color: selectedUsageType === type.value ? '#7c3aed' : '#374151',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  {selectedUsageType === type.value && <Check size={14} />}
                  <span style={{ marginLeft: selectedUsageType === type.value ? 0 : '22px' }}>
                    {type.label}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Active filter badge */}
        {selectedUsageType !== 'all' && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.25rem 0.5rem',
            backgroundColor: '#f3e8ff',
            color: '#7c3aed',
            borderRadius: '9999px',
            fontSize: '0.75rem',
            fontWeight: '500',
            flexShrink: 0
          }}>
            {usageTypes.find(t => t.value === selectedUsageType)?.label}
            <button
              onClick={() => setSelectedUsageType('all')}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '14px',
                height: '14px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                color: '#7c3aed',
                padding: 0
              }}
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* Spacer */}
        <div style={{ flex: 1 }} />

        {/* Bulk Actions */}
        {selectedItems.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {selectedItems.length} selected
            </span>
            <button
              onClick={handleBulkDelete}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.375rem 0.75rem',
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.75rem'
              }}
            >
              <Trash2 size={14} />
              Delete
            </button>
            <button
              onClick={() => setSelectedItems([])}
              style={{
                padding: '0.375rem 0.75rem',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.75rem'
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Media Grid */}
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '200px',
          color: '#6b7280'
        }}>
          Loading media...
        </div>
      ) : filteredMedia.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '3rem', 
          color: '#6b7280',
          backgroundColor: '#f9fafb',
          borderRadius: '0.5rem',
          border: '2px dashed #e5e7eb'
        }}>
          <Image size={48} style={{ margin: '0 auto 1rem', opacity: 0.3 }} />
          <p style={{ margin: 0, fontWeight: '500' }}>
            {searchTerm ? 'No media found matching your search' : 'No media uploaded yet'}
          </p>
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
            Upload images to use in your email communications
          </p>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
          gap: '1rem'
        }}>
          {filteredMedia.map((item) => (
            <div
              key={item.element_id}
              className="media-grid-item"
              style={{
                position: 'relative',
                aspectRatio: '1',
                borderRadius: '0.5rem',
                overflow: 'hidden',
                border: selectedItems.includes(item.element_id) 
                  ? '3px solid #7c3aed' 
                  : '1px solid #e5e7eb',
                cursor: 'pointer',
                backgroundColor: '#f9fafb',
                transition: 'all 0.15s'
              }}
              onClick={() => toggleSelect(item.element_id)}
            >
              <img
                src={getThumbnailUrl(item.source)}
                alt={item.title || 'Media'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
                loading="lazy"
              />
              
              {/* Selection checkbox */}
              <div style={{
                position: 'absolute',
                top: '0.5rem',
                left: '0.5rem',
                width: '1.25rem',
                height: '1.25rem',
                borderRadius: '0.25rem',
                backgroundColor: selectedItems.includes(item.element_id) ? '#7c3aed' : 'rgba(255,255,255,0.9)',
                border: selectedItems.includes(item.element_id) ? 'none' : '1px solid #d1d5db',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {selectedItems.includes(item.element_id) && (
                  <Check size={14} color="white" />
                )}
              </div>

              {/* Usage badge */}
              {item.usage_type && (
                <span style={{
                  position: 'absolute',
                  bottom: '0.5rem',
                  left: '0.5rem',
                  padding: '0.125rem 0.375rem',
                  fontSize: '0.625rem',
                  fontWeight: '500',
                  borderRadius: '0.25rem',
                  backgroundColor: getUsageColor(item.usage_type),
                  color: 'white',
                  textTransform: 'uppercase'
                }}>
                  {item.usage_type.replace('_', ' ')}
                </span>
              )}

              {/* Actions overlay */}
              <div 
                className="media-item-actions"
                style={{
                  position: 'absolute',
                  top: '0.5rem',
                  right: '0.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  opacity: 0,
                  transition: 'opacity 0.15s'
                }}
              >
                <button
                  onClick={(e) => openEditModal(item, e)}
                  title="Edit"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    color: '#374151'
                  }}
                >
                  <Edit2 size={14} />
                </button>
                <button
                  onClick={(e) => copyUrl(item.source, e)}
                  title="Copy URL"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    color: '#374151'
                  }}
                >
                  <Copy size={14} />
                </button>
                <button
                  onClick={(e) => handleDelete(item.element_id, e)}
                  title="Delete"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    border: 'none',
                    borderRadius: '0.25rem',
                    cursor: 'pointer',
                    color: '#dc2626'
                  }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setEditingItem(null)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '0.75rem',
              padding: '1.5rem',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: '600' }}>Edit Media</h3>
              <button
                onClick={() => setEditingItem(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '32px',
                  height: '32px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  color: '#6b7280',
                  borderRadius: '0.25rem'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Preview */}
            <div style={{ marginBottom: '1rem' }}>
              <img
                src={getThumbnailUrl(editingItem.source)}
                alt={editingItem.title || 'Preview'}
                style={{
                  width: '100%',
                  maxHeight: '200px',
                  objectFit: 'contain',
                  borderRadius: '0.5rem',
                  backgroundColor: '#f9fafb'
                }}
              />
            </div>

            {/* Form */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', color: '#374151' }}>
                  Title
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  placeholder="Enter a title..."
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', color: '#374151' }}>
                  Description
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    minHeight: '80px',
                    resize: 'vertical'
                  }}
                  placeholder="Enter a description..."
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', color: '#374151' }}>
                  Type
                </label>
                <select
                  value={editForm.usage_type}
                  onChange={(e) => setEditForm(prev => ({ ...prev, usage_type: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="promo">Promo</option>
                  <option value="social_media">Social Media</option>
                  <option value="attachment">Attachment</option>
                  <option value="banner">Banner</option>
                  <option value="logo">Logo</option>
                  <option value="general">General</option>
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', color: '#374151' }}>
                  Tags <span style={{ fontWeight: '400', color: '#9ca3af' }}>(comma separated)</span>
                </label>
                <input
                  type="text"
                  value={editForm.tags}
                  onChange={(e) => setEditForm(prev => ({ ...prev, tags: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  placeholder="e.g., header, newsletter, 2026"
                />
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
              <button
                onClick={() => setEditingItem(null)}
                style={{
                  padding: '0.5rem 1rem',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.375rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  backgroundColor: '#7c3aed',
                  color: 'white',
                  fontSize: '0.875rem',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1
                }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS for hover effects */}
      <style>{`
        .media-grid-item:hover .media-item-actions {
          opacity: 1 !important;
        }
        .media-grid-item:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
      `}</style>
    </div>
  );
};

export default MediaLibraryTab;
